/**
 * Fachregeln für Zeiteinträge: Validierung, Pausenregel (§4 ArbZG),
 * Überlappungsprüfung, MiLoG-Nachtragskennzeichnung.
 */
import {
  berlinDate, daysBetween, entryInterval, grossMinutes, isValidDate, isValidTime,
} from './time.js';

export interface EntryTimes {
  id: string;
  date: string;
  start: string;
  /** null = Stempeluhr läuft noch */
  end: string | null;
  pauseMin: number;
}

/** Frist nach §17 MiLoG: Aufzeichnung spätestens bis zum 7. Tag nach dem Arbeitstag. */
export const MILOG_DAYS = 7;

/**
 * Warnungen zur gesetzlichen Pause (§4 ArbZG):
 * mehr als 6 h Arbeit ⇒ mind. 30 min, mehr als 9 h ⇒ mind. 45 min.
 * Arbeitszeit = Anwesenheit − Pause.
 */
export function breakWarnings(gross: number, pauseMin: number): string[] {
  const work = gross - pauseMin;
  const warnings: string[] = [];
  if (work > 9 * 60 && pauseMin < 45) {
    warnings.push('Mehr als 9 Stunden Arbeitszeit: gesetzlich mindestens 45 Minuten Pause.');
  } else if (work > 6 * 60 && pauseMin < 30) {
    warnings.push('Mehr als 6 Stunden Arbeitszeit: gesetzlich mindestens 30 Minuten Pause.');
  }
  if (work > 10 * 60) warnings.push('Mehr als 10 Stunden Arbeitszeit am Tag sind nach ArbZG nicht zulässig.');
  return warnings;
}

export interface ValidationResult {
  errors: string[];
  warnings: string[];
}

/** Prüft einen einzelnen Eintrag (ohne Überlappung). */
export function validateEntry(e: EntryTimes): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!isValidDate(e.date)) errors.push('Ungültiges Datum.');
  if (!isValidTime(e.start)) errors.push('Ungültige Beginnzeit.');
  if (e.end !== null && !isValidTime(e.end)) errors.push('Ungültige Endzeit.');
  if (!Number.isInteger(e.pauseMin) || e.pauseMin < 0) errors.push('Pause muss eine nicht-negative Minutenzahl sein.');
  if (errors.length) return { errors, warnings };
  if (e.end === null) return { errors, warnings };
  if (e.start === e.end) {
    errors.push('Ende muss nach dem Beginn liegen.');
    return { errors, warnings };
  }
  const gross = grossMinutes(e.date, e.start, e.end);
  if (gross > 16 * 60) errors.push('Ein Eintrag darf höchstens 16 Stunden umfassen – bitte prüfen.');
  if (e.pauseMin >= gross) errors.push('Die Pause ist länger als die Arbeitszeit.');
  if (!errors.length) warnings.push(...breakWarnings(gross, e.pauseMin));
  return { errors, warnings };
}

/** Intervall in ms; laufende Einträge gelten bis „jetzt" (mindestens 1 min). */
export function intervalOf(e: EntryTimes, nowMs: number): [number, number] {
  if (e.end === null) {
    const iv = entryInterval(e.date, e.start, e.start === '23:59' ? '00:00' : nextMinute(e.start));
    return [iv.startMs, Math.max(iv.endMs, nowMs)];
  }
  const iv = entryInterval(e.date, e.start, e.end);
  return [iv.startMs, iv.endMs];
}

function nextMinute(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const n = h * 60 + m + 1;
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
}

/** Liefert alle Einträge aus `others`, die sich mit `entry` überschneiden (halboffene Intervalle). */
export function findOverlaps<T extends EntryTimes>(entry: EntryTimes, others: T[], nowMs = Date.now()): T[] {
  const [s, e] = intervalOf(entry, nowMs);
  return others.filter((o) => {
    if (o.id === entry.id) return false;
    const [os, oe] = intervalOf(o, nowMs);
    return s < oe && os < e;
  });
}

/**
 * Nachtrag später als 7 Tage nach dem Arbeitstag erfasst? (§17 MiLoG)
 * `recordedAtMs` = Zeitpunkt der ersten Erfassung.
 */
export function isLateRecording(date: string, recordedAtMs: number): boolean {
  return daysBetween(date, berlinDate(recordedAtMs)) > MILOG_DAYS;
}
